import React from "react";
import { X, Package, Truck, MapPin, CreditCard, Loader2, ExternalLink } from "lucide-react";
import StatusBadge from "./StatusBadge";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) +
    ", " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function parseOrderItems(orderItems) {
  if (!orderItems) return [];
  try {
    const parsed = typeof orderItems === "string" ? JSON.parse(orderItems) : orderItems;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function OrderDetailPanel({ order, isOpen, onClose, loadingItems }) {
  if (!isOpen || !order) return null;

  const items = parseOrderItems(order.orderItems || order.items);
  // Handle both field name conventions (orders table vs review_requests)
  const purchaseDate = order.purchaseDate || order.orderDate;
  const orderId = order.channelOrderId || order.amazonOrderId;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} />

      {/* Slide-over panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-[700px] bg-white shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 z-10">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-gray-900">{orderId}</h3>
                {order.platform && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                    {order.platform}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <StatusBadge status={order.orderStatus} />
                {order.paymentStatus && <StatusBadge status={order.paymentStatus} />}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Section 1: Order Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Package size={16} className="text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-700">Order Summary</h4>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Purchase Date</p>
                <p className="text-gray-800 font-medium">{formatDateTime(purchaseDate)}</p>
              </div>
              <div>
                <p className="text-gray-500">Last Updated</p>
                <p className="text-gray-800">{formatDateTime(order.lastUpdateDate)}</p>
              </div>
              <div>
                <p className="text-gray-500">Fulfillment</p>
                <p className="text-gray-800">{order.fulfillmentChannel === "AFN" ? "FBA" : order.fulfillmentChannel === "MFN" ? "FBM" : (order.fulfillmentChannel || "-")}</p>
              </div>
              <div>
                <p className="text-gray-500">Items</p>
                <p className="text-gray-800">{order.totalQuantity || order.quantity || (order.numberOfItemsShipped || 0) + (order.numberOfItemsUnshipped || 0) || "-"}</p>
              </div>
              {order.shipServiceLevel && (
                <div>
                  <p className="text-gray-500">Ship Service</p>
                  <p className="text-gray-800">{order.shipServiceLevel}</p>
                </div>
              )}
              {order.sellerName && (
                <div>
                  <p className="text-gray-500">Seller</p>
                  <p className="text-gray-800">{order.sellerName}</p>
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Shipping Info */}
          {(order.buyerCity || order.buyerState || order.buyerName) && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} className="text-gray-500" />
                <h4 className="text-sm font-semibold text-gray-700">Shipping Info</h4>
              </div>
              <div className="text-sm">
                {order.buyerName && (
                  <div className="mb-2">
                    <p className="text-gray-500">Buyer</p>
                    <p className="text-gray-800 font-medium">{order.buyerName}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500">Ship To</p>
                  <p className="text-gray-800">
                    {[order.buyerCity, order.buyerState, order.buyerPostalCode].filter(Boolean).join(", ")}
                    {order.buyerCountry && <><br />{order.buyerCountry}</>}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Section 3: Delivery Dates */}
          {(order.earliestDeliveryDate || order.latestDeliveryDate || order.latestShipDate || order.deliveredDate) && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Truck size={16} className="text-gray-500" />
                <h4 className="text-sm font-semibold text-gray-700">Delivery Timeline</h4>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {(order.latestShipDate) && (
                  <div>
                    <p className="text-gray-500">Ship By</p>
                    <p className="text-gray-800">{formatDateTime(order.latestShipDate)}</p>
                  </div>
                )}
                {(order.earliestDeliveryDate || order.deliveredDate) && (
                  <div>
                    <p className="text-gray-500">Est. Delivery</p>
                    <p className="text-gray-800">{formatDateTime(order.earliestDeliveryDate || order.deliveredDate)}</p>
                  </div>
                )}
                {order.latestDeliveryDate && (
                  <div>
                    <p className="text-gray-500">Latest Delivery</p>
                    <p className="text-gray-800">{formatDateTime(order.latestDeliveryDate)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section 4: Order Items */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Package size={16} className="text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-700">Order Items</h4>
            </div>
            {loadingItems ? (
              <div className="flex items-center justify-center py-8 gap-2">
                <Loader2 size={16} className="animate-spin text-[#f08804]" />
                <span className="text-sm text-gray-500">Loading order items...</span>
              </div>
            ) : items.length > 0 ? (
              <div className="border rounded-lg overflow-hidden bg-white">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-[50px]"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.map((item, i) => (
                      <tr key={i}>
                        <td className="px-3 py-3">
                          <div className="min-w-0">
                            <p className="text-sm text-gray-800 truncate max-w-[280px]" title={item.title}>
                              {item.title || "-"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {item.asin && <span className="font-mono">ASIN: {item.asin}</span>}
                              {item.asin && item.sku && <span> | </span>}
                              {item.sku && <span>SKU: {item.sku}</span>}
                            </p>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-700 text-right">{item.quantity || 1}</td>
                        <td className="px-3 py-3 text-sm text-gray-700 text-right">
                          {item.price ? formatINR(parseFloat(item.price)) : "-"}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {item.asin && (
                            <a
                              href={`https://www.amazon.in/dp/${item.asin}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-300 hover:text-[#f08804] transition-colors"
                              title="View on Amazon"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink size={13} />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">
                No items loaded yet. Items are fetched on first view.
              </p>
            )}
          </div>

          {/* Section 5: Order Total */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard size={16} className="text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-700">Order Total</h4>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between font-semibold">
                <span className="text-gray-700">Total</span>
                <span className="text-gray-900 text-base">
                  {order.orderTotal ? formatINR(parseFloat(order.orderTotal)) : "-"}
                </span>
              </div>
              {order.currencyCode && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Currency</span>
                  <span className="text-gray-600">{order.currencyCode}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
